import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../api'; // ← ajuste le chemin si nécessaire
import './formLogin.css';

function FormLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const reqCtlRef = useRef(null);

  const emailRegex = /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/;

  const validateForm = () => {
    const emailError = emailRegex.test(formData.email) ? '' : 'Adresse email invalide.';
    const passwordError =
      formData.password.length >= 6 && formData.password.length <= 20
        ? ''
        : 'Le mot de passe doit contenir entre 6 et 20 caractères.';
    setErrors({ email: emailError, password: passwordError });
    return !emailError && !passwordError;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setMsg({ text: '❌ Veuillez corriger les erreurs dans le formulaire.', type: 'error' });
      return;
    }

    // Annule une tentative précédente encore en cours
    if (reqCtlRef.current) reqCtlRef.current.abort();
    reqCtlRef.current = new AbortController();

    setIsLoading(true);
    setMsg({ text: '', type: '' });

    try {
      // --- LOGIN (via axios + baseURL propre) ---
      // Timeout plus long pour tolérer un éventuel "cold start" Render
      const { data } = await API.post('/api/auth/login', formData, {
        signal: reqCtlRef.current.signal,
        timeout: 15000,
        withCredentials: true,
      });

      // Stockage des infos session
      if (data?.token) localStorage.setItem('token', data.token);
      localStorage.setItem('role', data.role);
      localStorage.setItem('userId', data.user._id);
      localStorage.setItem('prenom', data.user.prenom || '');
      localStorage.setItem('nom', data.user.nom || '');
      localStorage.setItem('email', data.user.email || '');
      localStorage.setItem('telephone', data.user.telephone || '');
      localStorage.setItem('abonnement', data.abonnement || data.user.abonnement || '');
      localStorage.setItem(
        'proCanPostMissions',
        data.user?.proReview?.missionsAccess === true ? 'true' : 'false'
      );

      // notifier l'app (Header, etc.)
      window.dispatchEvent(new CustomEvent('auth:changed', { detail: { action: 'login' } }));

      // --- SI PRO : vérifier s'il a au moins une annonce ---
      if (data.role === 'pro') {
        try {
          // L'interceptor ajoute le token s'il est en localStorage
          // Timeout court pour ne pas bloquer la connexion si la route est lente
          const resAnn = await API.get(`/api/annonces/by-user/${data.user._id}`, {
            signal: reqCtlRef.current.signal,
            timeout: 5000,
            withCredentials: true,
          });
          const annData = resAnn.data;

          const hasAnnonce = Array.isArray(annData)
            ? annData.length > 0
            : !!(annData && annData._id);

          if (hasAnnonce) {
            navigate('/choisir-profil');
            return;
          }
        } catch {
          // En cas d'erreur (timeout, réseau, etc.), on ne bloque pas la connexion
          // et on retombe sur la home comme avant.
        }
      }

      // Par défaut : home
      navigate('/');
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        // requête annulée : on ne montre rien
        return;
      }

      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message;

      if (status === 403 && err?.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setMsg({
          text: serverMsg || '❌ Veuillez vérifier votre email avant de vous connecter.',
          type: 'error',
        });
      } else if (status === 401 || status === 400) {
        setMsg({ text: serverMsg || '❌ Email ou mot de passe incorrect', type: 'error' });
      } else if (err.code === 'ECONNABORTED') {
        setMsg({ text: '⏳ Le serveur met trop de temps à répondre. Réessayez.', type: 'error' });
      } else if (status === 0 || err.message?.includes('Network')) {
        setMsg({ text: '❌ Problème réseau ou CORS. Réessayez.', type: 'error' });
      } else {
        setMsg({ text: serverMsg || '❌ Erreur serveur, veuillez réessayer plus tard.', type: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupRedirect = () => navigate('/auth/signup/client');
  const handleForgotPassword = () => navigate('/auth/forgot-password');

  return (
    <div className='login'>
      <h2 className='container_login_title'>Connexion</h2>
      <form className='container_login' onSubmit={handleSubmit}>
        <div className='container_login_form'>
          <input
            className={`container_login_form_input ${errors.email ? 'input-error-email' : ''}`}
            name="email"
            type="email"
            placeholder='Email'
            onChange={handleChange}
            value={formData.email}
            autoComplete="username"
            disabled={isLoading}
          />
          {errors.email && <p className="error-message">{errors.email}</p>}

          {/* Mot de passe + icône œil */}
          <div style={{ position: "relative", width: "100%" }}>
            <input
              className={`container_login_form_input ${errors.password ? 'input-error-password' : ''}`}
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder='Mot de passe'
              onChange={handleChange}
              value={formData.password}
              style={{ width: "100%", paddingRight: 36 }}
              autoComplete="current-password"
              disabled={isLoading}
            />
            <span
              className="toggle-password-eye"
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: "absolute",
                right: 18,
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                fontSize: 19,
                color: "#765e47"
              }}
              title={showPassword ? "Masquer" : "Afficher"}
              aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showPassword ? "👁️" : "👁️‍🗨️"}
            </span>
          </div>
          {errors.password && <p className="error-message">{errors.password}</p>}

          {/* Bouton mot de passe oublié */}
          <button
            type="button"
            className="forgot-password-btn"
            onClick={handleForgotPassword}
            style={{
              background: 'none',
              border: 'none',
              color: '#67503a',
              fontSize: '15px',
              textDecoration: 'underline',
              margin: '8px 0',
              cursor: 'pointer',
              alignSelf: 'flex-end'
            }}
            disabled={isLoading}
          >
            Mot de passe oublié ?
          </button>

          <button className='container_form_send' type="submit" disabled={isLoading}>
            {isLoading ? 'Connexion…' : 'Se connecter'}
          </button>

          {msg.text && <p className={`message ${msg.type}`}>{msg.text}</p>}

          <button
            type="button"
            className='container_form_signup'
            onClick={handleSignupRedirect}
            disabled={isLoading}
          >
            S'inscrire
          </button>
        </div>
      </form>
    </div>
  );
}

export default FormLogin;
